import { DOCUMENT } from '@angular/common';
import { ApplicationRef, ComponentFactoryResolver, EmbeddedViewRef, Inject, Injectable, Injector } from '@angular/core';
import { Actions, ofType, createEffect, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, concatMap, forkJoin, of, delayWhen } from 'rxjs';
import {
    switchMap, map, filter, take, tap, first, distinctUntilChanged,
} from 'rxjs/operators';

import { ChatComponent } from '../chat/chat.component';
import { HubtypeService } from '../hubtype/hubtype.service';
import { ChatType } from '../models/chat.models';
import { ChatService } from '../services/chat.service';

import {
    ChatComponentInitilized,
    CloseChat,
    CreateChatComponent,
    CreateChatComponentFailed,
    CreateChatComponentSuccess,
    DestroyChatComponent,
    DestroyChatComponentSuccess,
    EnableHubtype,
    EnableKakao,
    EnableMocha,
    EnableUrlChat,
    HideChatButton,
    IncrementEnabledChats,
    InitHubtypeWidget,
    InitHubtypeWidgetSuccess,
    InitializeChat,
    InitializeChatSuccess,
    OpenChat,
    OpenHubtype,
    OpenHubtypeSuccess,
    ResetEnabledChats,
    SetLiveHelp,
    SetScheduleText,
    ShowChatButton,
    ToggleChat,
    ToggleChatSuccess,
} from './chat.actions';
import { getEnabledChats, selectActiveChat, selectChatOpened, selectChatVisible, getHubtype, selectChatWidgetLoaded, selectChatHtmlLoaded } from './chat.selectors';

import { StoreAndLanguage } from '@core/domain/state/core.state';
import { LoadLibrary } from '@core/infrastructure/state/core-commands.actions';
import { getCmsConfigValue, getCurrentLanguage, getLibraries, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { UiService } from '@core/services/ui.service';
import { PublicUser } from '@features/user';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { sendObservabilityLog } from '@shared/analytics/ITXObservabilityCore/helper';
import { getConfiguredDelayByPage } from '@shared/chat/application/uses-cases/chat-delay.use-case';
import { getUserSelector } from '@shared/user/store';

@Injectable()
export class ChatEffects {
    private readonly hubTypeChatCfg$ = getCmsConfigValue('chatbot_hubtype');

    private readonly urlTypeChatCfg$ = getCmsConfigValue('chatbot_url');

    private readonly kakaoTypeChatCfg$ = getCmsConfigValue('kakao_talk');

    private readonly chatDelayConfig$ = getCmsConfigValue('chat_delay');

    private readonly pageName$ = this.store.select(routerPublicQueries.getDataFromRouter).pipe(
        filter((page) => !!page?.pageName),
        map((params) => params.pageName),
        distinctUntilChanged()
    );

    constructor(
        private readonly actions$: Actions,
        private readonly chatService: ChatService,
        private readonly hubTypeService: HubtypeService,
        private readonly storageService: CoreStorageService,
        private readonly store: Store,
        private readonly translate: TranslateService,
        private readonly uiService: UiService,

        @Inject(DOCUMENT) private readonly document: Document,
        private readonly appRef: ApplicationRef,
        // eslint-disable-next-line deprecation/deprecation
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly injector: Injector
    ) {}

    public initilizeOnPageChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChatComponentInitilized),
            map(() => InitializeChat())
        );
    });

    public initUrlChat$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ResetEnabledChats),
            switchMap(() => {
                return this.store.select(this.urlTypeChatCfg$).pipe(
                    take(1),
                    filter((urlChatCfg) => {
                        return urlChatCfg?.active;
                    }),
                );
            }),
            map((urlChatCfg) => EnableUrlChat({ payload: urlChatCfg })),
        );
    });

    public initKakaoChat$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ResetEnabledChats),
            switchMap(() => {
                return this.store.select(this.kakaoTypeChatCfg$).pipe(
                    take(1),
                    filter((kakaoChatCfg) => {
                        return kakaoChatCfg?.active;
                    }),
                );
            }),
            map((kakaoChatCfg) => EnableKakao({ payload: kakaoChatCfg })),
        );
    });

    public initMochaChat$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ResetEnabledChats),
            switchMap(() => {
                return this.chatService.scheduleInfo(false).pipe(take(1)).pipe(
                    filter(({ valid }) => valid),
                    map(({ active, scheduleMessage }) => {
                        if (active) {
                            return EnableMocha({ scheduleMessage });
                        }
                        return SetScheduleText({ scheduleMessage });
                    }),
                );
            }),
        );
    });

    public resetEnabledChats$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                InitializeChat,
            ),
            map(() => ResetEnabledChats())
        );
    });

    public incrementEnabledChats$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                EnableHubtype,
                EnableUrlChat,
                EnableKakao,
                EnableMocha,
            ),
            map(() => IncrementEnabledChats({ quantity: 1 }))
        );
    });

    public setChatAsVisible$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                EnableHubtype,
                EnableUrlChat,
                EnableKakao,
                EnableMocha
            ),
            switchMap(() => this.store.select(selectChatVisible).pipe(
                take(1),
                filter((isChatVisible) => !isChatVisible),
            )),
            map(() => InitializeChatSuccess({ visible: true }))
        );
    });

    public setLiveHelp$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                IncrementEnabledChats,
            ),
            switchMap(() => this.store.select(getEnabledChats).pipe(
                take(1),
                filter((enabledChats) => enabledChats === 2),
            )),
            map(() => SetLiveHelp())
        );
    });

    public ToggleChat$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ToggleChat),
            concatLatestFrom(() => {
                return [
                    this.store.select(selectActiveChat),
                    this.store.select(selectChatOpened),
                    this.store.select(getLibraries),
                ];
            }),
            filter(([_payload, activeChat, _shouldOpen, libs]) => !!activeChat && libs?.botonic?.loaded),
            map(([_payload, activeChat, shouldOpen]) => {
                if (activeChat === ChatType.HUBTYPE && shouldOpen) {
                    return OpenHubtype();
                }
                return ToggleChatSuccess();
            })
        );
    });

    public initHubtypeChat$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ResetEnabledChats),
            switchMap(() => {
                return forkJoin([
                    this.chatService.scheduleInfo(true).pipe(take(1)),
                    this.store.select(this.hubTypeChatCfg$).pipe(take(1)),
                    this.store.select(getCurrentLanguage).pipe(take(1)),
                ]).pipe(
                    filter(([{ valid }]) => valid),
                    map(([scheduleConfig, hubtypeConfig, currentLang]) => {
                        const shouldBeActived = scheduleConfig.active
                        && this.hubTypeService.checkIfHubtypeIsActive(hubtypeConfig, currentLang.id);

                        if (shouldBeActived) {
                            return EnableHubtype({ payload: { ...hubtypeConfig, ...scheduleConfig } });
                        }
                        return SetScheduleText({ scheduleMessage: scheduleConfig.scheduleMessage });
                    }),
                );
            }),
        );
    });

    public initializeHubType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ToggleChat, OpenChat),
            switchMap(() => forkJoin([
                this.store.select(selectActiveChat).pipe(filter(active => !!active), take(1)),
            ])),
            concatLatestFrom(() => {
                return [
                    this.store.select(selectChatOpened),
                    this.pageName$.pipe(map((page: any) => page === 'ItxChatPage')),
                ];
            }),
            filter(([[activeChat], shouldOpen, isPage]) =>
                activeChat === ChatType.HUBTYPE && (shouldOpen || isPage)),
            switchMap(() => this.store.select(getHubtype).pipe(take(1))),
            map(({ configuration }) => {
                return InitHubtypeWidget({ configuration });
            })
        );
    });

    public loadHubTypeLibrary$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitHubtypeWidget),
            concatLatestFrom(() => [ this.store.select(getLibraries) ]),
            filter(([_, libs]) => !libs?.botonic?.loaded),
            map(([{ configuration }]) => LoadLibrary({ library: 'botonic', src: configuration.url }))
        );
    });

    public initHubTypeWidget$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitHubtypeWidget),
            concatLatestFrom(() => [
                this.store.select(getUserSelector),
                this.store.select(getStoreAndLanguage),
                this.store.select(selectChatWidgetLoaded),
                this.store.select(selectChatHtmlLoaded),
            ]),
            filter(([_, user, __, widgetLoaded, htmlLoaded]) => !widgetLoaded && htmlLoaded && !!user),
            switchMap(([{ configuration }, user, storeAndLanguage]) => {
                // Wait until the botonic library is loaded
                return this.store.select(getLibraries).pipe(
                    filter((libs) => libs?.botonic?.loaded),
                    first(),
                    map((_libraryLoaded) => [
                        { configuration },
                        user,
                        storeAndLanguage,
                    ] as [{ configuration: any }, PublicUser, StoreAndLanguage])
                );
            }),
            switchMap(([{ configuration }, user, storeAndLanguage]) => {
                const translations = (configuration.menu || [])
                    .map((element) => element.label ?? element.closeLabel)
                    .concat([configuration.placeHolder, configuration.title]);

                return forkJoin({
                    configuration: of({ ...configuration }),
                    storeAndLanguage: of(storeAndLanguage),
                    translations: this.translate.get(translations),
                    user: of(user),
                });
            }),
            tap(({
                configuration, translations, user, storeAndLanguage,
            }) => {
                this.hubTypeService.initWidget(configuration, translations, user, storeAndLanguage);
            }),
            map(() => InitHubtypeWidgetSuccess()),
        );
    });

    public openHubTypeChat$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                InitHubtypeWidgetSuccess,
                OpenHubtype,
            ),
            tap(() => {
                if (!this.uiService.ui.webapp) {
                    // A debatir si se usa esta propiedad o bien una generica para todos los chats que persista si está activo o no
                    this.storageService.setValue('hubtypeInUse', true, 'PERMANENT', 'TECHNICAL');
                }

                setTimeout(() => { this.chatService.openHubtype(); });
            }),
            map(() => OpenHubtypeSuccess())
        );
    });

    public openHubTypeChatAtStartup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EnableHubtype),
            switchMap(() => combineLatest([
                this.store.select(selectChatOpened).pipe(first()),
                this.store.select(selectActiveChat).pipe(first()),
            ])),
            filter(([isOpen, activeChat]) =>
                !isOpen && activeChat === ChatType.HUBTYPE && !!this.storageService.getValue<boolean>('hubtypeInUse')),
            map(() => ToggleChat())
        );
    });

    public createChatComponent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateChatComponent),
            concatMap(() => ChatEffects.GetInitData(this.store)),
            map(([page, sections]) => [page, ChatEffects.CanCreateChatComponent(page, sections)]),
            concatLatestFrom(() => [this.store.select(selectChatHtmlLoaded)]),
            map(([[page, canCreate], htmlLoaded]) => {
                if (canCreate) {
                    if (htmlLoaded || page === 'ItxChatPage') {
                        // si se puede activar y ya existe, no hacemos nada
                        // si es Chat Page, ya está inyectado en su HTML
                        return CreateChatComponentSuccess();
                    }
                    // si se puede activar y no existe, inyectamos
                    const injected = ChatEffects.InjectChatComponent(
                        this.componentFactoryResolver,
                        this.injector,
                        this.appRef,
                        this.document
                    );
                    return injected
                        ? CreateChatComponentSuccess()
                        : CreateChatComponentFailed();
                }
                // si no se puede activar, destruimos
                return DestroyChatComponent();
            }),
        );
    });

    public destroyChatComponent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DestroyChatComponent),
            concatLatestFrom(() => [this.store.select(selectChatHtmlLoaded)]),
            map((htmlElement) => {
                this.store.dispatch(CloseChat());
                if (htmlElement) {
                    ChatEffects.UninjectChatComponent(this.document);
                }
                return DestroyChatComponentSuccess();
            }),
        );
    });

    public destroyHubtypeComponent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DestroyChatComponent),
            concatLatestFrom(() => [this.store.select(selectActiveChat)]),
            filter(([_, activeChat]) => activeChat === ChatType.HUBTYPE),
            map(() => {
                this.storageService.removeValue('hubtypeInUse');
                Botonic.onMessage = undefined;
            }),
        );
    }, { dispatch: false });

    public chatPage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateChatComponentSuccess),
            tap(() => { this.store.dispatch(HideChatButton()); }),
            // Abrimos el chat por defecto en la página de chat
            concatLatestFrom(() => [this.pageName$]),
            filter(([_, page]: any) => page === 'ItxChatPage'),
            map(() => OpenChat()),
        );
    });

    public chatDelay$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HideChatButton),
            concatLatestFrom(() => [this.pageName$, this.store.select(this.chatDelayConfig$).pipe(take(1))]),
            delayWhen(([_, page, configDelay]) => getConfiguredDelayByPage(page, configDelay)),
            map(() => ShowChatButton()),
        );
    });

    ///

    private static GetInitData(store: Store): Observable<[string, string[]]> {
        return combineLatest([
            store.select(routerPublicQueries.getDataFromRouter).pipe(
                filter((page) => !!page?.pageName),
                map((params) => params?.pageName),
                distinctUntilChanged()
            ),
            store.select(getCmsConfigValue('chatbot_hubtype')).pipe(
                filter((config) => !!config),
                take(1),
                map((config) => config.sections),
            ),
        ]);
    }

    private static CanCreateChatComponent(page: string, sections: string[]): boolean {
        return sections.includes(page);
    }

    private static InjectChatComponent(
        // eslint-disable-next-line deprecation/deprecation
        resolver: ComponentFactoryResolver,
        injector: Injector,
        appRef: ApplicationRef,
        document: Document
    ): boolean {
        try {
            const componentRef = resolver
                .resolveComponentFactory(ChatComponent)
                .create(injector);

            appRef.attachView(componentRef.hostView);

            const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
                .rootNodes[0] as HTMLElement;
            return !!document.body.appendChild(domElem);
        } catch (err) {
            sendObservabilityLog(err, 'InjectChatComponent');
            console.error(err);
            return false;
        }
    }

    private static UninjectChatComponent(document: Document) {
        try {
            const chat = document.getElementsByTagName('chat')[0];
            if (chat) {
                chat.remove();
            }
        } catch (err) {
            sendObservabilityLog(err, 'UninjectChatComponent');
            console.error(err);
        }
    }
}
